import {
  AfterViewInit,
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { faSmile } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import {
  DialogCloseOptions,
  DialogCloseReason,
  injectDirtyCloseHandler,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';

import { PostApiService } from 'src/app/api/services/post-api.service';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';

import { NomSentiment } from 'src/app/core/constants';
import { User } from 'src/app/core/models/core.models';

import { injectSetPostSentimentMutation } from 'src/app/api/queries/post.queries';

import { take, tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { NgpRovingFocusGroup } from 'ng-primitives/roving-focus';
import { NgpRadioGroup } from 'ng-primitives/radio';
import { SuiRadioGroupDirective, SuiRadioComponent } from '@sui/radio';
import { TranslatePipe } from '@ngx-translate/core';

interface SentimentManualChange {
  user: User;
  sentiment: number;
  time_scored: string;
}

interface DialogData {
  sentimentValue: any;
  inputSentimentId: number;
  feedId: string;
  postId: string;
}
interface DialogOutput {
  sentimentId: string;
}

@Component({
  selector: 'app-post-sentiment-dialog',
  templateUrl: './post-sentiment-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './post-sentiment-dialog.component.scss',
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    FaIconComponent,
    SuiButtonComponent,
    SuiDialogContentSlot,
    NgpRovingFocusGroup,
    NgpRadioGroup,
    SuiRadioGroupDirective,
    FormsModule,
    ReactiveFormsModule,
    SuiRadioComponent,
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class PostSentimentDialogComponent
  extends SimpleDialogBase<DialogData, DialogOutput>
  implements AfterViewInit
{
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);
  private postApi = inject(PostApiService);

  // The dialog closes immediately after `performSave()`, so the cache patch lives in
  // the hook - a per-call callback would be dropped with the destroyed component.
  private readonly sentimentMutation = injectSetPostSentimentMutation();

  actionIcon: IconDefinition = faSmile;
  dialogTitle: string = this.translateService.instant(
    'post-sentiment.title.sentiment-editor',
  );

  sentimentControl = new FormControl(null, [Validators.required]);
  private isDirty = computed(() => this.sentimentControl.dirty);
  private isValid = computed(() => this.sentimentControl.valid);

  sentimentManualChanges: SentimentManualChange[] = [];

  sentimentManualChangesFields = [
    {
      id: 1,
      label: 'avatar',
      value: (change) => change?.user.avatar_url,
      isImage: true,
    },
    {
      id: 2,
      label: 'name',
      value: (change) => change?.user.display_name,
    },
    {
      id: 3,
      label: 'score',
      value: (change) => change?.time_scored,
    },
    {
      id: 4,
      label: 'sentiment',
      value: (change) => this.getSentimentLabel(change?.sentiment),
    },
  ];
  sentimentValues = [
    {
      value: -1,
      label: this.translateService.instant(
        'post-sentiment.sentiment-value.negative',
      ),
    },
    {
      value: 0,
      label: this.translateService.instant(
        'post-sentiment.sentiment-value.neutral',
      ),
    },
    {
      value: 1,
      label: this.translateService.instant(
        'post-sentiment.sentiment-value.positive',
      ),
    },
  ];

  set sentimentValue(value) {
    this.dialogData.sentimentValue = value;
  }

  get sentimentValue() {
    return this.dialogData.sentimentValue ?? 0;
  }

  get inputSentimentId() {
    return this.dialogData.inputSentimentId ?? 0;
  }

  get feedId() {
    return this.dialogData.feedId;
  }

  get postId() {
    return this.dialogData.postId;
  }

  ngOnInit(): void {
    if (this.postId) {
      this.postApi
        .getSentimentManualScore(this.postId)
        .pipe(
          take(1),
          tap((sentimentManualChanges) => {
            this.sentimentManualChanges = sentimentManualChanges;
          }),
        )
        .subscribe();

      this.sentimentValue = this.getSentimentValue(this.inputSentimentId);
      this.sentimentControl.setValue(this.sentimentValue);
    }

    this.sentimentControl.valueChanges.subscribe((val) => {
      this.sentimentValue = val;
    });
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  getSentimentValue(value: number) {
    const sentimentMap: Map<number, number> = new Map<number, number>([
      [NomSentiment.NEGATIVE, -1],
      [NomSentiment.NEUTRAL, 0],
      [NomSentiment.POSITIVE, 1],
    ]);

    return sentimentMap.get(value);
  }

  getSentimentLabel(value: number) {
    const sentimentLabelMap: Map<number, string> = new Map<number, string>([
      [-1, 'Negative'],
      [0, 'Neutral'],
      [1, 'Positive'],
    ]);

    return sentimentLabelMap.get(value);
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    if (this.postId) {
      this.sentimentMutation.mutate({
        feedId: this.feedId,
        postId: this.postId,
        sentiment: this.sentimentValue,
      });
    }

    return {
      reason: DialogCloseReason.Save,
      data: { sentimentId: this.sentimentValue },
    };
  }

  save(): void {
    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
